import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth-store';
import { useProjects } from '@/api/queries';
import { logout as logoutRequest } from '@/api/client';
import { stopProactiveRefresh } from '@/lib/api';
import { cn } from '@/lib/cn';
import { StatusChip } from './StatusChip';

/**
 * The chassis.
 *
 * One rounded object floating on a lit ground, holding a white rail on the left
 * and a white stage beside it. There is no dark top bar any more: the client
 * surface is light throughout, and the only large dark area on it is the
 * primary button.
 *
 * The rail is the navigation, because one chat per project means the project
 * *is* the address. With a single project — the common case — it collapses to a
 * slim horizontal strip that keeps the brand and the account menu and drops the
 * list, rather than disappearing and leaving the account menu homeless.
 */

export function AppShell({ children }: { children: ReactNode }) {
  const projects = useProjects();
  const location = useLocation();

  const showList = (projects.data?.length ?? 0) > 1;

  return (
    <div className="chassis flex flex-col">
      <div
        className={cn(
          'flex min-h-0 flex-1 gap-[22px] p-[22px]',
          'max-[900px]:flex-col max-[900px]:gap-3 max-[900px]:p-3',
          '[@media(max-height:680px)]:gap-2.5 [@media(max-height:680px)]:p-2.5',
          !showList && 'flex-col',
        )}
      >
        <Rail showList={showList} projects={projects.data ?? []} />

        <main
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-bone-raised max-[900px]:rounded-md"
          key={location.pathname}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

type RailProject = NonNullable<ReturnType<typeof useProjects>['data']>[number];

function Rail({ showList, projects }: { showList: boolean; projects: RailProject[] }) {
  return (
    <nav
      aria-label="Your chats"
      className={cn(
        'flex min-h-0 flex-none rounded-xl bg-bone-raised',
        showList
          ? 'w-[262px] flex-col px-3.5 pb-3.5 pt-[22px] max-[900px]:w-auto max-[900px]:flex-row max-[900px]:items-center max-[900px]:gap-2.5 max-[900px]:rounded-md max-[900px]:px-3.5 max-[900px]:py-2.5'
          : 'flex-row items-center gap-3 rounded-md px-4 py-3.5',
      )}
    >
      <div className={cn(showList ? 'px-3.5 pb-[22px] max-[900px]:p-0' : 'p-0')}>
        <Link to="/" className="flex items-center gap-2.5 rounded-full" aria-label="Your chats">
          <Mark className="h-[30px] w-[30px] text-graphite-900" />
          <span className="text-[1.3125rem] font-bold leading-none tracking-[-0.035em] text-graphite-900">hourz</span>
        </Link>
      </div>

      {showList ? (
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto max-[900px]:hidden">
          <p className="eyebrow px-2.5 pb-3">Your chats</p>
          <ul>
            {projects.map((project) => (
              <li key={project.id}>
                <NavLink
                  to={`/projects/${project.id}`}
                  className={({ isActive }) =>
                    cn(
                      'mb-1 block rounded-md px-3.5 py-3 transition-colors',
                      isActive ? 'bg-bone shadow-[inset_0_0_0_1.5px_var(--rule-strong)]' : 'hover:bg-bone',
                    )
                  }
                >
                  <span className="block truncate text-[0.9375rem] font-semibold tracking-[-0.015em] text-graphite-900">
                    {project.name}
                  </span>
                  <StatusChip
                    status={project.status}
                    size="sm"
                    className="mt-2"
                    awaitingAnswer={project.awaiting_answer ?? false}
                  />
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <AccountMenu openUpwards={showList} />
    </nav>
  );
}

/**
 * The account menu.
 *
 * It lives at the foot of the rail rather than in a header, so the one piece of
 * furniture that is not about the work sits furthest from it. In the full rail
 * it opens upwards; in the slim strip there is nothing above it, so it drops.
 */
function AccountMenu({ openUpwards }: { openUpwards: boolean }) {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const signOut = async () => {
    stopProactiveRefresh();
    try {
      await logoutRequest();
    } finally {
      clear();
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        'relative',
        openUpwards ? 'mt-auto pt-2.5 max-[900px]:ml-auto max-[900px]:mt-0 max-[900px]:pt-0' : 'ml-auto',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex w-full items-center gap-3 rounded-md px-3.5 py-3 text-left hover:bg-bone"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-mint text-[0.8125rem] font-semibold tracking-[-0.01em] text-graphite-900">
          {initials(user?.display_name)}
        </span>
        <span
          className={cn(
            'truncate text-[0.90625rem] font-medium text-graphite-500',
            openUpwards ? 'max-[900px]:hidden' : '',
          )}
        >
          {user?.display_name}
        </span>
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className={cn(
              'absolute z-20 animate-panel-in overflow-hidden rounded-md bg-bone-raised p-2 shadow-panel',
              openUpwards
                ? 'bottom-16 left-0 right-0 max-[900px]:bottom-auto max-[900px]:left-auto max-[900px]:right-0 max-[900px]:top-14 max-[900px]:w-[246px]'
                : 'right-0 top-[58px] w-[246px]',
            )}
          >
            <div className="px-3.5 pb-3.5 pt-3">
              <p className="truncate text-[0.9375rem] font-semibold text-graphite-900">{user?.display_name}</p>
              {user?.email ? (
                <p className="truncate font-mono text-[0.75rem] text-graphite-500">{user.email}</p>
              ) : null}
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => void signOut()}
              className="block w-full rounded px-3.5 py-2.5 text-left text-[0.90625rem] font-medium text-graphite-900 hover:bg-bone"
            >
              Sign out
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function initials(name: string | undefined): string {
  if (!name) return '·';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '·';
}

/** The mark: an hour dial, its elapsed sector filled, with the next tick beside it. */
export function Mark({ className }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 42 42" className={cn('h-[30px] w-[30px]', className)}>
      <path
        d="M17 2a15 15 0 1 0 0 30 15 15 0 0 0 0-30Zm0 8.4a6.6 6.6 0 1 1 0 13.2 6.6 6.6 0 0 1 0-13.2Z"
        fill="currentColor"
      />
      <path d="M17 17V4.4A12.6 12.6 0 0 1 29.6 17Z" fill="currentColor" />
      <circle cx="36.4" cy="28.4" r="4.6" fill="currentColor" />
    </svg>
  );
}
